'use strict';

import { queryFirst, loadScript, removeClass, addClass, matchesBreakpoint, hasClass, queryAll } from '../domUtil';
const { showQuickview } = require('../quickView/styliticsQuickView').default;
const { styliticsTemplates } = require('../templates');
const { TIMEOUT_100 } = require('../constants');
const STYLITICS_INACTIVE_CLASS = 'is-faded';
const MENU_CHANGE_TIMEOUT = 2000;

const $body = $('body');
const styliticsWidget = queryFirst('#stylitics-widget');
const styliticsSettingsElement = queryFirst('.stylitics-widget-settings');
const styliticsSettingsData = styliticsSettingsElement.dataset || null;
const quickViewBaseURL = styliticsSettingsData.quickviewUrl || null;
const itemColorName = styliticsSettingsData.itemColorName || 'Color';

/**
 * Waits until the specified Stylitics bundle loses the inactive class, which means it is ready to be clicked on
 * @param {HTMLElement} element - Specific element to monitor for mutations
 * @returns {Promise} - A Promise to either be resolved or rejected depending on if the class is removed or not
 */
function waitForActiveCard(element) {
    return new Promise((resolve, reject) => {
        if (!hasClass(element, STYLITICS_INACTIVE_CLASS)) return resolve();
        const observer = new MutationObserver((mutations, self) => {
            if (!hasClass(element, STYLITICS_INACTIVE_CLASS)) {
                self.disconnect();
                resolve();
            }
        });
        observer.observe(element, { attributes: true, childList: false, subtree: false });
        setTimeout(() => {
            observer.disconnect();
            reject(new Error('waitForActiveCard timed out'));
        }, MENU_CHANGE_TIMEOUT);
    });
}

/**
 * Initializes the Stylitics Widget and adds an event listener to refresh the widget on swatch change.
 * @param {string} styliticsId - Shop ID for our stylitics account
 * @param {string} trackingEnv - Determines which environment to send Stylitics tracking data to
 * @param {number} minBundles - Minimum number of recommended bundles to display in the widget
 * @param {number} maxBundles - Maximum number of recommended bundles to display in the widget
 * @param {boolean} quickviewEnabled - Flag to enable/disable quickview when clicking on Stylitics items
 * @param {boolean} testMode - Set to true to use test data instead of live data
 * @param {Array} testProducts - Array of test product Variant Group IDs. If testMode is true, Stylitics will load these products, instead
 */
function initializeStylitics(styliticsId, trackingEnv, minBundles, maxBundles, quickviewEnabled, testMode, testProducts) {
    const isTestMode = testMode === 'true';
    if (!styliticsId || !minBundles || !maxBundles || (isTestMode && !testProducts) || !StyliticsClassicWidget) return;
    const selectedSwatch = queryFirst('.swatch-circle.selected').closest('.color-attribute');
    const variantGroup = !isTestMode ? selectedSwatch.dataset.productId : testProducts[0];
    if (!variantGroup) return;
    const widgetInstance = new StyliticsClassicWidget(styliticsId, 'stylitics-products', {
        api: {
            item_number: variantGroup,
            min: minBundles,
            max: maxBundles
        },
        navigation: {
            clickItemLinkDesktop: 'new-tab',
            clickItemLinkMobile: 'new-tab'
        },
        price: {
            hideDoubleZeroCents: true
        },
        text: {
            viewDetailsCTA: 'In this look',
            itemLinkCTA: 'Shop now',
            itemDetailsModalHeader: 'In this look'
        },
        display: {
            disableMnM: true,
            clickableCarouselDots: false,
            clickableCarouselAdvancementRate: 3,
            clickableCarouselNextItemPeek: 0,
            clickableCarouselGutterWidth: 4,
            swipeableCarouselNextItemPeek: 0.041,
            swipeableCarouselGutterWidth: 4,
            bundleBackgroundColor: 'transparent'
        },
        trackingEnv,
        customer: {
            profileIdPath: 'utag_data.sfcc_customer_id'
        },
        // IMPORTANT: These breakpoints are NOT based on viewport width but rather the width of the widget container.
        responsive: [
            [0, { columns: 1 }],
            [767, { columns: 2 }],
            [870, { columns: 3 }]
        ]
    });

    // Once suggestion bundles have been loaded, show widget if enough bundles have been loaded
    widgetInstance.on('mount', 'bundles', data => {
        if (data.bundles && data.bundles.length >= minBundles) {
            setTimeout(() => {
                initializeCustomScrollbar();
            }, TIMEOUT_100);
        }
    });

    // Triggers event for each bundle that's loaded
    widgetInstance.on('mount', 'bundle', data => {
        const { bundle, element } = data;
        // If this is the bundle that matches the outfit in the PDP imagery, add a badge
        if (bundle['on-model-image']) {
            const badge = document.createElement('div');
            addClass(badge, 'stylitics-badge-block');
            badge.innerHTML = styliticsTemplates.shopTheModelBadge;
            queryFirst('.stylitics-bundle-badge', element).appendChild(badge);
        }
    });

    // Update widget when swatch changes
    $body.on('swatchChangeEvent', (event, el) => {
        if(!el) return;
        const isColorSwatch = el.closest('.color-attribute');
        const isQuickview = el.closest('.product-quickview');
        // If the event is coming from an active quickview or isn't targeting a color swatch, then ignore it
        if (isQuickview || !isColorSwatch) return;
        const newVariantGroup = !isTestMode ? el.dataset.productId : testProducts[1] || testProducts[0];
        if (newVariantGroup) {
            widgetInstance.refresh({ api: { item_number: newVariantGroup } });
        }
    });

    // Overrides the default stylitics fade out animation before the element is removed from the DOM.
    $body.on('click', '.stylitics-close, .stylitics-classic-bundlecard-wrap.is-faded', event => {
        const activeBundle = queryFirst('.stylitics-bundle-details');
        addClass(activeBundle, 'fade-out');
    });

    // Override default item click behavior to show quickview on desktop breakpoints
    widgetInstance.override('click', 'item', product => {
        const { item, element, bundle } = product;
        if (!item || !element) return;
        const productURL = item.affiliate_link;
        const productID = item.remote_id;
        if (matchesBreakpoint('lg') && quickviewEnabled && quickViewBaseURL && productID) {
            $(styliticsWidget).trigger('stylitics:click_product', [{ item, bundle, itemElem: element, pdpType: 'quickview' }]);
            showQuickview(element, quickViewBaseURL + productID);
        } else if (productURL) {
            $(styliticsWidget).trigger('stylitics:click_product', [{ item, bundle, itemElem: element, pdpType: 'regular' }]);
            window.open(productURL, '_blank');
        }
    });

    widgetInstance.on('click', 'bundle', bundleData => {
        const { element } = bundleData;
        const inactiveCard = element.closest(`.${STYLITICS_INACTIVE_CLASS}`);
        if (inactiveCard) {
            // If the clicked bundle is currently inactive, wait for it to become active before triggering a click event
            waitForActiveCard(inactiveCard).then(() => {
                $(element).trigger('click');
            });
        } else {
            // We only want to send an anayltics event once, when the menu is clicked while in an "active" state
            $(styliticsWidget).trigger('stylitics:click_bundle', [bundleData]);
        }
    });

    // Set additional class to positioned the modal
    widgetInstance.on('click', 'bundle', data => {
        const { element } = data;
        const parentSequenceEl = element.closest('.stylitics-sequence');
        if (!parentSequenceEl) return;
        const scrollLeft = parseInt(getComputedStyle(parentSequenceEl)?.getPropertyValue("--sequence-translateX")?.trim().match(/\d+/));
        element.closest('.stylitics-classic-bundlecard-wrap').style.left = scrollLeft + 'px';

        $(styliticsWidget).trigger('stylitics:click_bundle', [data]);
    });

    // eslint-disable-next-line require-jsdoc
    function initializeCustomScrollbar() {
        if (!styliticsWidget) {
            return;
        }

        // Create custom scrollbar
        const customScrollbar = document.createElement('div');
        addClass(customScrollbar, 'custom-scrollbar');

        const scrollThumb = document.createElement('div');
        addClass(scrollThumb, 'scroll-thumb');
        customScrollbar.appendChild(scrollThumb);
        styliticsWidget.appendChild(customScrollbar);

        const scrollContainer = queryFirst('.stylitics-sequence', styliticsWidget);
        if (!scrollContainer) {
            return;
        }

        setupCustomScrollbar(scrollContainer, scrollThumb);
    }

    // eslint-disable-next-line require-jsdoc
    function setupCustomScrollbar(scrollContainer, scrollThumb) {
        // eslint-disable-next-line require-jsdoc
        function updateScrollbar() {
            const isMobile = !matchesBreakpoint('md');
            const isTablet = (matchesBreakpoint('md') && !matchesBreakpoint('lg'));

            // eslint-disable-next-line
            const customPadding = isMobile ? 40 : isTablet ? window.innerWidth / 7.5 : 0;

            const containerWidth = scrollContainer.offsetWidth;
            const contentWidth = scrollContainer.scrollWidth;
            const trackWidth = containerWidth - customPadding;
            const scrollRatio = trackWidth / contentWidth;

            const thumbWidth = scrollRatio * containerWidth;
            scrollThumb.style.width = `${thumbWidth}px`;

            const scrollLeft = scrollContainer.scrollLeft;
            const thumbLeft = scrollLeft * scrollRatio;
            scrollThumb.style.left = `${thumbLeft}px`;
        }

        scrollContainer.addEventListener('scroll', updateScrollbar);

        updateScrollbar();
        window.addEventListener('resize', updateScrollbar);
    }

    // Start widget instance
    widgetInstance.start();
}

// Asynchronously load Stylitics script. Once it's loaded, initialize the widget
window.addEventListener('load', event => {
    if (styliticsSettingsData) {
        const minBundles = parseInt(styliticsSettingsData.minBundles, 10);
        const maxBundles = parseInt(styliticsSettingsData.maxBundles, 10);
        const testProducts = styliticsSettingsData.testProducts ? styliticsSettingsData.testProducts.split(',') : null;
        const quickviewEnabled = styliticsSettingsData.quickviewEnabled === 'true';
        loadScript(styliticsSettingsData.styliticsScript, true).then(result =>
            initializeStylitics(styliticsSettingsData.styliticsId, styliticsSettingsData.trackingEnv, minBundles, maxBundles, quickviewEnabled, styliticsSettingsData.testMode, testProducts)
        );
    }
});
